import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { ChevronDown, ChevronRight, ChevronLeft } from 'lucide-react';
import { getIconComponent } from '../lib/categoryIcons';

const API_URL = process.env.REACT_APP_BACKEND_URL;


export const CategoryNavbar = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeSubcategory, setActiveSubcategory] = useState(null);
  const [rawCategories, setRawCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedMobileCategory, setSelectedMobileCategory] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0 });
  
  // Carousel state
  const scrollContainerRef = useRef(null);
  const navbarRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Check scroll position and update arrow visibility
  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 5);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
    }
  }, []);

  // Scroll handler
  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 200; // pixels to scroll
      const newScrollLeft = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
      // Cleanup close timeout on unmount
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Fetch categories from database
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Try backend first (works in preview)
        try {
          const response = await fetch(`${API_URL}/api/categories/public`);
          if (response.ok) {
            const data = await response.json();
            if (data.categories && data.categories.length > 0) {
              setRawCategories(data.categories);
              setLoading(false);
              return;
            }
          }
        } catch (backendError) {
          console.log('Backend not available, fetching from Supabase directly');
        }
        
        // Fallback: fetch directly from Supabase (for production)
        // Filter only active categories (is_active = true or null for backwards compatibility)
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .or('is_active.eq.true,is_active.is.null')
          .order('sort_order', { ascending: true });
        
        if (!error && data && data.length > 0) {
          setRawCategories(data);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Build category tree from flat database data
  const categories = useMemo(() => {
    // If no data from API, return empty array (no hardcoded fallback)
    if (!rawCategories.length) return [];

    // Get main categories (no parent)
    const mainCategories = rawCategories
      .filter(c => !c.parent_id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

    // Build tree structure matching original format
    return mainCategories.map(main => {
      // Get subcategories (level 2) for this main category
      const subcategories = rawCategories
        .filter(c => c.parent_id === main.id)
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map(sub => {
          // Get children (level 3) for this subcategory
          const children = rawCategories
            .filter(c => c.parent_id === sub.id)
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            .map(child => ({
              name: child.name,
              slug: child.slug,
              icon: child.icon
            }));

          return {
            id: sub.id,
            name: sub.name,
            slug: sub.slug,
            icon: getIconComponent(sub.icon),
            children
          };
        });

      return {
        id: main.slug,
        name: main.name,
        icon: getIconComponent(main.icon),
        subcategories
      };
    });
  }, [rawCategories]);

  // Update scroll buttons when categories change or on mount/resize
  useEffect(() => {
    // Small delay to ensure DOM is updated
    const timer = setTimeout(() => {
      updateScrollButtons();
    }, 100);
    
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
    }
    return () => {
      clearTimeout(timer);
      if (container) {
        container.removeEventListener('scroll', updateScrollButtons);
      }
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [updateScrollButtons, categories]);

  const handleCategoryEnter = (categoryId, event) => {
    if (!isMobile) {
      // Cancel any pending close timeout
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      
      setActiveCategory(categoryId);
      setActiveSubcategory(null);
      
      // Calculate menu position (fixed positioning relative to viewport)
      if (event && event.currentTarget) {
        const button = event.currentTarget;
        const buttonRect = button.getBoundingClientRect();
        const menuWidth = 580; // min-w-[580px]
        const viewportWidth = window.innerWidth;
        const padding = 16; // padding from edge
        
        // Calculate left position
        let leftPosition = buttonRect.left;
        
        // Check if menu would overflow right edge
        if (leftPosition + menuWidth > viewportWidth - padding) {
          leftPosition = viewportWidth - padding - menuWidth;
        }
        
        // Check if menu would overflow left edge
        if (leftPosition < padding) {
          leftPosition = padding;
        }
        
        setMenuPosition({ 
          left: leftPosition,
          top: buttonRect.bottom + 4 // 4px gap below the button
        });
      }
    }
  };

  const handleCategoryLeave = () => {
    if (!isMobile) {
      // Add a small delay before closing to allow mouse to reach the mega menu
      closeTimeoutRef.current = setTimeout(() => {
        setActiveCategory(null);
        setActiveSubcategory(null);
      }, 150); // 150ms delay
    }
  };

  const handleMegaMenuEnter = () => {
    // Cancel close timeout when mouse enters mega menu
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMegaMenuLeave = () => {
    // Close immediately when leaving mega menu
    setActiveCategory(null);
    setActiveSubcategory(null);
  };

  const handleMobileCategoryClick = (category) => {
    setSelectedMobileCategory(category);
    setMobileMenuOpen(true);
    setActiveSubcategory(null);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setSelectedMobileCategory(null);
    setActiveSubcategory(null);
  };

  return (
    <>
      <div className="relative bg-white border-b border-slate-200 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {/* Desktop Carousel Container */}
          <div 
            className="relative hidden md:block"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Left Gradient + Arrow */}
            <div 
              className={`absolute left-0 top-0 bottom-0 z-10 flex items-center transition-opacity duration-300 ${
                canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              {/* Gradient fade */}
              <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none" />
              {/* Arrow button */}
              <button
                onClick={() => handleScroll('left')}
                className={`relative z-10 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white border border-slate-200 rounded-full shadow-md hover:shadow-lg transition-all duration-200 ml-1 ${
                  isHovering ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'
                }`}
                aria-label="Scorri a sinistra"
              >
                <ChevronLeft className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Right Gradient + Arrow */}
            <div 
              className={`absolute right-0 top-0 bottom-0 z-10 flex items-center transition-opacity duration-300 ${
                canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              {/* Gradient fade */}
              <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none" />
              {/* Arrow button */}
              <button
                onClick={() => handleScroll('right')}
                className={`relative z-10 w-8 h-8 flex items-center justify-center bg-white/90 hover:bg-white border border-slate-200 rounded-full shadow-md hover:shadow-lg transition-all duration-200 mr-1 ${
                  isHovering ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2'
                }`}
                aria-label="Scorri a destra"
              >
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            </div>

            {/* Scrollable Categories Container - Desktop */}
            <div 
              ref={scrollContainerRef}
              className="flex items-center justify-start h-10 gap-0.5 overflow-x-auto scrollbar-hide scroll-smooth"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* Spacer for centering when few categories */}
              <div className="flex-shrink-0 flex-grow" />
              
              {categories.map((category) => {
                const IconComponent = category.icon;
                return (
                  <div
                    key={category.id}
                    className="relative flex-shrink-0"
                    onMouseEnter={(e) => handleCategoryEnter(category.id, e)}
                    onMouseLeave={handleCategoryLeave}
                  >
                    <button
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium whitespace-nowrap rounded-lg transition-colors ${
                        activeCategory === category.id
                          ? 'bg-[#008080]/10 text-[#008080]'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                      data-testid={`category-nav-${category.id}`}
                    >
                      <IconComponent className="w-4 h-4 text-[#008080]" />
                      <span>{category.name}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${activeCategory === category.id ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                );
              })}
            
            {/* Spacer for centering when few categories */}
            <div className="flex-shrink-0 flex-grow" />
          </div>
        </div>

        {/* Desktop Mega Menu - Rendered outside scroll container with fixed positioning */}
        {!isMobile && activeCategory && (() => {
          const category = categories.find(c => c.id === activeCategory);
          if (!category) return null;
          
          return (
            <div 
              className="fixed bg-white border border-slate-200 rounded-xl shadow-2xl flex min-w-[580px] hidden md:flex"
              style={{ 
                zIndex: 9999,
                left: `${menuPosition.left}px`,
                top: `${menuPosition.top}px`
              }}
              onMouseEnter={handleMegaMenuEnter}
              onMouseLeave={handleMegaMenuLeave}
            >
              {/* Left column - Subcategories */}
              <div className="w-[220px] bg-slate-50 rounded-l-xl p-3 border-r border-slate-100">
                <div className="space-y-0.5">
                  {category.subcategories.map((subcat, idx) => {
                    const SubIcon = subcat.icon;
                    return (
                      <div
                        key={idx}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer transition-all ${
                          activeSubcategory === idx
                            ? 'bg-white shadow-sm text-[#008080]'
                            : 'hover:bg-white/60 text-slate-700'
                        }`}
                        onMouseEnter={() => setActiveSubcategory(idx)}
                      >
                        <div className="flex items-center gap-2.5">
                          <SubIcon className={`w-4 h-4 ${activeSubcategory === idx ? 'text-[#008080]' : 'text-slate-500'}`} />
                          <span className="text-sm font-medium">{subcat.name}</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 ${activeSubcategory === idx ? 'text-[#008080]' : 'text-slate-400'}`} />
                      </div>
                    );
                  })}
                </div>
                
                {/* View All Link */}
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <Link
                    to={`/categoria/${category.id}`}
                    className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-[#008080] hover:bg-[#008080]/5 rounded-lg transition-colors"
                    onClick={handleCategoryLeave}
                  >
                    Vedi tutti in {category.name.split(' ')[0]}
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Right column - Children (third level) */}
              <div className="flex-1 p-5 min-h-[280px]">
                {activeSubcategory !== null && category.subcategories[activeSubcategory] ? (
                  <>
                    <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                      {React.createElement(category.subcategories[activeSubcategory].icon, { className: 'w-5 h-5 text-[#008080]' })}
                      {category.subcategories[activeSubcategory].name}
                    </h3>
                    
                    {category.subcategories[activeSubcategory].children && 
                     category.subcategories[activeSubcategory].children.length > 0 ? (
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                        {category.subcategories[activeSubcategory].children.map((child, childIdx) => (
                          <Link
                            key={childIdx}
                            to={`/categoria/${category.id}/${category.subcategories[activeSubcategory].slug}/${child.slug}`}
                            className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#008080] py-2 px-3 rounded-lg hover:bg-[#008080]/5 transition-colors group"
                            onClick={handleCategoryLeave}
                          >
                            <ChevronRight className="w-3 h-3 text-slate-300 group-hover:text-[#008080] transition-colors" />
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 mb-4">
                        Scopri tutti i prodotti artigianali in questa categoria.
                      </p>
                    )}
                    
                    {/* Quick link to subcategory */}
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <Link
                        to={`/categoria/${category.id}/${category.subcategories[activeSubcategory].slug}`}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-[#008080] hover:text-[#006666] bg-[#008080]/5 hover:bg-[#008080]/10 px-4 py-2 rounded-lg transition-colors"
                        onClick={handleCategoryLeave}
                      >
                        Vedi tutti in {category.subcategories[activeSubcategory].name}
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-slate-400">
                    <p className="text-sm">Passa il mouse su una sottocategoria</p>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Mobile Scrollable Categories */}
        <div className="md:hidden flex items-center h-10 gap-0.5 overflow-x-auto scrollbar-hide px-1">
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => handleMobileCategoryClick(category)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium whitespace-nowrap rounded-lg transition-colors flex-shrink-0 ${
                  selectedMobileCategory?.id === category.id && mobileMenuOpen
                    ? 'bg-[#008080]/10 text-[#008080]'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
                data-testid={`category-nav-mobile-${category.id}`}
              >
                <IconComponent className="w-4 h-4 text-[#008080]" />
                <span>{category.name.split(' ')[0]}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            );
          })}
        </div>
      </div>
    </div>

    {/* Mobile Bottom Sheet Menu */}
    {isMobile && mobileMenuOpen && selectedMobileCategory && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/40 z-[9998]"
            onClick={closeMobileMenu}
          />
          
          {/* Bottom Sheet */}
          <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[9999] max-h-[80vh] overflow-hidden animate-in slide-in-from-bottom duration-300">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {React.createElement(selectedMobileCategory.icon, { className: 'w-5 h-5 text-[#008080]' })}
                <h2 className="font-bold text-slate-900">{selectedMobileCategory.name}</h2>
              </div>
              <button 
                onClick={closeMobileMenu}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(80vh-60px)]">
              {/* View All Main Category */}
              <Link
                to={`/categoria/${selectedMobileCategory.id}`}
                className="flex items-center justify-between px-4 py-3 bg-[#008080]/5 border-b border-slate-100"
                onClick={closeMobileMenu}
              >
                <span className="font-semibold text-[#008080]">Vedi tutti in {selectedMobileCategory.name.split(' ')[0]}</span>
                <ChevronRight className="w-5 h-5 text-[#008080]" />
              </Link>

              {/* Subcategories */}
              <div className="divide-y divide-slate-100">
                {selectedMobileCategory.subcategories.map((subcat, idx) => {
                  const SubIcon = subcat.icon;
                  const isExpanded = activeSubcategory === idx;
                  
                  return (
                    <div key={idx}>
                      <button
                        onClick={() => setActiveSubcategory(isExpanded ? null : idx)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left"
                      >
                        <div className="flex items-center gap-3">
                          <SubIcon className="w-5 h-5 text-slate-500" />
                          <span className="font-medium text-slate-800">{subcat.name}</span>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Expanded Children */}
                      {isExpanded && (
                        <div className="bg-slate-50 px-4 py-2">
                          <Link
                            to={`/categoria/${selectedMobileCategory.id}/${subcat.slug}`}
                            className="flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-[#008080] rounded-lg"
                            onClick={closeMobileMenu}
                          >
                            Vedi tutti in {subcat.name}
                          </Link>
                          
                          {subcat.children && subcat.children.length > 0 && (
                            <div className="space-y-1 mt-1">
                              {subcat.children.map((child, childIdx) => (
                                <Link
                                  key={childIdx}
                                  to={`/categoria/${selectedMobileCategory.id}/${subcat.slug}/${child.slug}`}
                                  className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 rounded-lg active:bg-slate-100"
                                  onClick={closeMobileMenu}
                                >
                                  <ChevronRight className="w-3 h-3 text-slate-400" />
                                  {child.name}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CategoryNavbar;
